import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  makeStyles,
  CircularProgress,
} from '@material-ui/core';
import BuildIcon from '@material-ui/icons/Build';

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.background.default,
  },
  paper: {
    padding: theme.spacing(4),
    textAlign: 'center',
    maxWidth: 500,
    width: '100%',
  },
  icon: {
    fontSize: 64,
    color: theme.palette.warning.main,
    marginBottom: theme.spacing(2),
  },
  title: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  message: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
  },
  loading: {
    marginTop: theme.spacing(2),
  },
}));

interface MaintenanceModeProps {
  children: React.ReactNode;
}

interface MaintenanceData {
  message?: string;
  estimatedTime?: string;
}

const MAINTENANCE_FILE_URL = '/maintenance.json';

export default function MaintenanceMode({ children }: MaintenanceModeProps) {
  const classes = useStyles();
  const [isMaintenanceMode, setIsMaintenanceMode] = useState<boolean | null>(
    null,
  );
  const [maintenanceData, setMaintenanceData] = useState<MaintenanceData>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkMaintenanceMode = async () => {
      try {
        const response = await fetch(MAINTENANCE_FILE_URL, {
          method: 'HEAD',
          cache: 'no-cache', // Prevent caching to get real-time status
        });

        if (response.ok) {
          // Maintenance file exists, get its content
          const dataResponse = await fetch(MAINTENANCE_FILE_URL, {
            cache: 'no-cache',
          });

          if (dataResponse.ok) {
            try {
              const data = await dataResponse.json();
              setMaintenanceData(data);
            } catch {
              // If JSON parsing fails, use default message
              setMaintenanceData({});
            }
          }

          setIsMaintenanceMode(true);
        } else {
          setIsMaintenanceMode(false);
        }
      } catch (error) {
        // If there's an error (network, etc.), assume normal mode
        setIsMaintenanceMode(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkMaintenanceMode();

    // Check every 30 seconds for maintenance mode changes
    const interval = setInterval(checkMaintenanceMode, 30000);
    return () => clearInterval(interval);
  }, []);

  if (isLoading) {
    return (
      <Box className={classes.root}>
        <Container>
          <Box display='flex' justifyContent='center' alignItems='center'>
            <CircularProgress className={classes.loading} />
          </Box>
        </Container>
      </Box>
    );
  }

  if (isMaintenanceMode) {
    return (
      <Box className={classes.root}>
        <Container>
          <Paper className={classes.paper} elevation={3}>
            <BuildIcon className={classes.icon} />
            <Typography variant='h4' component='h1' className={classes.title}>
              Maintenance in Progress
            </Typography>
            <Typography variant='body1' className={classes.message}>
              {maintenanceData.message ||
                'We are currently performing scheduled maintenance to improve our service. Please check back soon.'}
            </Typography>
            {maintenanceData.estimatedTime && (
              <Typography variant='body2' color='textSecondary'>
                Estimated completion: {maintenanceData.estimatedTime}
              </Typography>
            )}
            <Typography
              variant='body2'
              color='textSecondary'
              style={{ marginTop: 16 }}
            >
              This page will automatically refresh when maintenance is complete.
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  return <>{children}</>;
}
